import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Timezone for Albania (CET/CEST)
const TIMEZONE = 'Europe/Tirane';

// Albanian month names
const ALBANIAN_MONTHS = [
  'Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor',
  'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'
];

const ALBANIAN_MONTHS_SHORT = [
  'Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer',
  'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'
];

/**
 * Convert UTC date to Albanian timezone (Europe/Tirane)
 */
function toAlbanianTime(dateInput) {
  if (!dateInput) return null;
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  
  // Convert to Albanian timezone
  const options = { timeZone: TIMEZONE };
  const albanianDateStr = date.toLocaleString('en-US', options);
  return new Date(albanianDateStr);
}

/**
 * Format currency: 615.00 LEK
 * Uses dot for decimals, no thousand separators
 */
export function formatCurrency(amount, currency = 'LEK') {
  if (amount === null || amount === undefined) return '-';
  const num = Number(amount);
  // Format with thousand separators and 2 decimals
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

export function extractApiErrorMessage(error, fallback = 'Gabim gjatë përpunimit të kërkesës') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.msg) {
          const field = Array.isArray(item.loc) ? item.loc.slice(1).join(' > ') : '';
          return field ? `${field}: ${item.msg}` : item.msg;
        }
        return null;
      })
      .filter(Boolean)
      .join(' | ');
  }

  const message = error?.response?.data?.message || error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

/**
 * Format energy: 615.00 kWh
 * Uses dot for decimals, thousand separators for large numbers
 */
export function formatEnergy(kwh) {
  if (kwh === null || kwh === undefined) return '-';
  const num = Number(kwh);
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${formatted} kWh`;
}

/**
 * Format number: 615.00
 * Uses dot for decimals, no thousand separators
 */
export function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined) return '-';
  return Number(num).toFixed(decimals);
}

/**
 * Format date in Albanian timezone: "16 Janar 2026, 14:30"
 * All dates are converted to Europe/Tirane (CET/CEST +1/+2)
 * @param {string|Date} dateInput - Date to format (UTC)
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string in Albanian timezone
 */
export function formatDate(dateInput, options = {}) {
  if (!dateInput) return '-';
  
  const date = toAlbanianTime(dateInput);
  if (!date) return '-';
  
  const {
    includeTime = true,
    shortMonth = false,
    onlyDate = false,
    onlyTime = false,
    includeSeconds = false
  } = options;
  
  const day = date.getDate();
  const month = shortMonth ? ALBANIAN_MONTHS_SHORT[date.getMonth()] : ALBANIAN_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  if (onlyTime) {
    return includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
  }
  
  if (onlyDate) {
    return `${day} ${month} ${year}`;
  }
  
  if (includeTime) {
    const timeStr = includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
    return `${day} ${month} ${year}, ${timeStr}`;
  }
  
  return `${day} ${month} ${year}`;
}

/**
 * Format date with seconds: "16 Janar 2026, 14:30:45"
 */
export function formatDateWithSeconds(dateInput) {
  return formatDate(dateInput, { includeTime: true, includeSeconds: true });
}

/**
 * Format date only (no time): "16 Janar 2026"
 */
export function formatDateOnly(dateInput) {
  return formatDate(dateInput, { onlyDate: true });
}

/**
 * Format time only: "14:30" (24h)
 */
export function formatTime(dateInput) {
  return formatDate(dateInput, { onlyTime: true });
}

/**
 * Format relative time in Albanian
 */
export function formatRelativeTime(dateInput) {
  if (!dateInput) return '-';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '-';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) {
    return 'Tani';
  } else if (diffMin < 60) {
    return `${diffMin} min më parë`;
  } else if (diffHour < 24) {
    return `${diffHour} orë më parë`;
  } else if (diffDay < 7) {
    return `${diffDay} ditë më parë`;
  } else {
    return formatDate(date, { includeTime: false });
  }
}

export function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '-';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  if (hours > 0) {
    if (mins === 0) {
      return `${hours} orë`;
    }
    return `${hours} orë ${mins} min`;
  }
  return `${mins} min`;
}

export function getStatusColor(status) {
  const statusMap = {
    'Available': 'status-available',
    'Charging': 'status-charging',
    'Occupied': 'status-charging',
    'Preparing': 'status-preparing',
    'Faulted': 'status-faulted',
    'Offline': 'status-offline',
    'Reconnecting': 'status-reconnecting',
    'SuspendedEVSE': 'status-preparing',
    'SuspendedEV': 'status-preparing',
    'Finishing': 'status-charging',
    'Reserved': 'status-preparing',
    'Unavailable': 'status-offline',
    'charging': 'status-charging',
    'completed': 'status-available',
    'failed': 'status-faulted'
  };
  return statusMap[status] || 'status-offline';
}

export function getStatusDotColor(status) {
  const colorMap = {
    'Available': 'bg-green-500',
    'Charging': 'bg-red-500',
    'Occupied': 'bg-red-500',
    'Preparing': 'bg-orange-500',
    'Faulted': 'bg-red-500',
    'Offline': 'bg-zinc-500',
    'Reconnecting': 'bg-yellow-500',
    'charging': 'bg-red-500',
    'completed': 'bg-green-500',
    'failed': 'bg-red-500'
  };
  return colorMap[status] || 'bg-zinc-500';
}

/**
 * Format Albanian date - alias for formatDate
 * @param {string|Date} dateInput 
 * @param {boolean} includeTime 
 */
export function formatAlbanianDate(dateInput, includeTime = true) {
  return formatDate(dateInput, { includeTime });
}
